import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CashbackType, useLabel } from '@atlas/core';
import { Icon, Label } from '@atlas/ui';

import { capitalize } from 'common/utils/capitalize';

import CashbackStatus from './CashbackStatus';

const cashbackNamesMap = new Map([[CashbackType.CASINO_CASHBACK_GGR, 'Casino']]);
const dateFormatter = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const CashBackItem = ({ cashback, handleActivate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    accrualFrequency,
    cashbackType,
    coefficient,
    createTime,
    finishAt,
    name,
    shortDescription,
  } = cashback;

  const handleIsExpanded = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded);
  }, [setIsExpanded]);

  const casinoCoefficient = useMemo(() => {
    return `${coefficient * 100}%`;
  }, [coefficient]);

  const cashbackPeriod = useMemo(() => {
    const formattedCreateTime = dateFormatter.format(createTime);
    const formattedFinishAt = dateFormatter.format(finishAt);

    return `${formattedCreateTime} - ${formattedFinishAt}`;
  }, [createTime, finishAt]);

  const typeName = useMemo(() => {
    return capitalize(cashbackNamesMap.get(cashbackType));
  }, [cashbackType]);

  const cashbackTypeName = useLabel(typeName);
  const cashbackAccrualFrequency = useLabel(`cashback_${accrualFrequency}`);

  return (
    <div
      className={cx('cashback__item', {
        active: isExpanded,
      })}
    >
      <div className="cashback__item__content" onClick={handleIsExpanded}>
        <div className="cashback__item__info">
          <span className="cashback__item__name">{name}</span>
          <span className="cashback__item__row">
            <span className="sub-title-text">
              <Label message="Product" />:
            </span>
            <span className="title-small-text">{cashbackTypeName}</span>
          </span>
        </div>
        <div className="arrow-container">
          <Icon className="icon-arrow-down" name="arrow-down" />
        </div>
      </div>
      <div className="cashback__item__status">
        <CashbackStatus cashback={cashback} handleActivate={handleActivate} />
      </div>
      {isExpanded && (
        <div className="cashback__item__expanded">
          <span className="cashback__item__row">
            <Label className="sub-title-text" message="cashback_percent" />:
            <span className="title-small-text">{casinoCoefficient}</span>
          </span>
          <span className="cashback__item__row">
            <Label className="sub-title-text" message="cashback_accrual_frequency" />:
            <span className="title-small-text">{capitalize(cashbackAccrualFrequency)}</span>
          </span>
          <span className="cashback__item__row">
            <Label className="sub-title-text" message="cashback_start_date_end_date" />:
            <span className="title-small-text">{cashbackPeriod}</span>
          </span>
          {shortDescription && (
            <span className="cashback__item__row cashback__item__row--description">
              <span className="sub-title-text">{shortDescription}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

CashBackItem.propTypes = {
  cashback: PropTypes.shape({
    accrualFrequency: PropTypes.string.isRequired,
    cashbackType: PropTypes.string.isRequired,
    coefficient: PropTypes.number.isRequired,
    createTime: PropTypes.number.isRequired,
    description: PropTypes.number.isRequired,
    finishAt: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  handleActivate: PropTypes.func.isRequired,
};

export default CashBackItem;
